import { useState } from "react";
import { useNavigate } from "react-router-dom";
import VideoPreview from "./components/VideoPreview";
import SmartTimeline from "./components/SmartTimeline";
import AIAssistPanel from "./components/AIAssistPanel";
import EditingToolsPanel from "./components/EditingToolsPanel";
import ClipVariations from "./components/ClipVariations";
import ActionBar from "./components/ActionBar";

export default function EditorPage() {
  const navigate = useNavigate();
  const [showPublishModal, setShowPublishModal] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0E14] flex flex-col overflow-hidden">
      {/* Top Bar */}
      <header className="flex items-center justify-between px-5 py-3 border-b border-white/6 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/onboarding")}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </span>
            <span className="text-sm">결과로 돌아가기</span>
          </button>
          <div className="w-px h-5 bg-white/8" />
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 cursor-pointer"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/35b72bf3-4b7f-4858-926b-243fdf4afa99.png"
              alt="StreamClip AI"
              className="h-6 w-6 object-contain"
            />
            <span
              className="text-white font-bold text-sm tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              StreamClip<span className="text-[#00D9FF]">AI</span>
            </span>
          </button>
        </div>

        {/* Clip title */}
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
          <span className="text-white text-sm font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            제가 그 종교에서 빠져나올 수 있었던 이유는요...
          </span>
          <span className="text-gray-500 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            · 0:14 · 바이럴 점수 97
          </span>
        </div>

        {/* Top right: clip variations */}
        <div className="flex items-center">
          <ClipVariations />
        </div>
      </header>

      {/* Main layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Editing Tools */}
        <aside className="w-64 shrink-0 border-r border-white/6 p-4 overflow-y-auto" style={{ scrollbarWidth: "none" }}>
          <EditingToolsPanel />
        </aside>

        {/* Center - Video + Timeline */}
        <main className="flex-1 flex flex-col overflow-hidden px-5 py-4 gap-4 min-w-0">
          {/* Video Preview */}
          <div className="flex-1 min-h-0">
            <VideoPreview />
          </div>

          {/* Smart Timeline */}
          <div className="shrink-0">
            <SmartTimeline />
          </div>

          {/* Action Bar */}
          <div className="shrink-0">
            <ActionBar onPublishClick={() => setShowPublishModal(true)} />
          </div>
        </main>

        {/* Right Panel - AI Assist */}
        <aside className="w-72 shrink-0 border-l border-white/6 p-4 overflow-y-auto" style={{ scrollbarWidth: "none" }}>
          <AIAssistPanel />
        </aside>
      </div>

      {/* Publish CTA Modal */}
      {showPublishModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(10,14,20,0.85)", backdropFilter: "blur(8px)" }}
        >
          <div className="relative w-full max-w-md mx-4 bg-[#0F1419] border border-white/10 rounded-2xl p-8">
            {/* Close */}
            <button
              onClick={() => setShowPublishModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>

            {/* Icon */}
            <div
              className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-5"
              style={{ background: "linear-gradient(135deg, #00D9FF22, #10B98122)", border: "1px solid rgba(0,217,255,0.25)" }}
            >
              <i className="ri-rocket-2-line text-[#00D9FF] text-2xl" />
            </div>

            <h3
              className="text-white text-xl font-bold text-center mb-2"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              결과물을 바로 배포하세요
            </h3>
            <p
              className="text-gray-400 text-sm text-center mb-6 leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              TikTok, YouTube Shorts, Instagram Reels에<br />
              클립을 즉시 게시하고 분석 데이터를 추적하세요
            </p>

            {/* Platform icons */}
            <div className="flex items-center justify-center gap-3 mb-6">
              {[
                { icon: "ri-tiktok-line", color: "#FF2D55", label: "TikTok" },
                { icon: "ri-youtube-line", color: "#FF0000", label: "Shorts" },
                { icon: "ri-instagram-line", color: "#E1306C", label: "Reels" },
              ].map((p) => (
                <div key={p.label} className="flex flex-col items-center gap-1.5">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl"
                    style={{ background: `${p.color}15`, border: `1px solid ${p.color}30` }}
                  >
                    <i className={`${p.icon} text-lg`} style={{ color: p.color }} />
                  </div>
                  <span className="text-gray-500 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{p.label}</span>
                </div>
              ))}
            </div>

            {/* Feature list */}
            <div className="space-y-2 mb-6">
              {[
                "원클릭 멀티 플랫폼 동시 게시",
                "클립 성과 실시간 분석 대시보드",
                "AI 자동 해시태그 및 최적 게시 시간 추천",
              ].map((feat) => (
                <div key={feat} className="flex items-center gap-2.5">
                  <span className="w-4 h-4 flex items-center justify-center rounded-full bg-[#10B981]/15 shrink-0">
                    <i className="ri-check-line text-[#10B981] text-xs" />
                  </span>
                  <span className="text-gray-300 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>{feat}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <button
              className="w-full py-3.5 rounded-xl text-[#0A0E14] font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
              style={{
                background: "linear-gradient(135deg, #00D9FF, #10B981)",
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            >
              무료 계정 만들고 게시하기
            </button>
            <button
              onClick={() => setShowPublishModal(false)}
              className="w-full mt-3 py-2.5 rounded-xl text-gray-500 hover:text-gray-300 text-sm transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              계속 편집하기
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
